import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import { useClient } from '@based/sdk/react'
import { match } from 'ts-pattern'
import Row from '@app/components/layout/Row'
import { Button } from '@app/components/core/button/Button'
import { Modal } from '@app/components/blocks/modal/Modal'
import { Text } from '@app/components/core/typography/Text'
import { CreatePageLayout } from '@app/pages/layout/CreatePageLayout'
import { LoaderWrapper } from '@app/components/blocks/loader/LoaderWrapper'
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { useTranslations } from '@i18n/useLanguage'
import { useToast } from '@app/util/hooks/useToast'
import { useAutoSave } from '@app/util/hooks/useAutoSave'
import { pages } from '@app/util/links'
import type {
  ExistingImage,
  ImagePreview,
} from '@app/components/blocks/imageEditRow/ImageEditRow'
import type { DraftEventModel } from 'functions/app/event/get-draft'

import {
  eventCreationSteps,
  eventCreationWorkflow,
  type EventCreationStep,
} from './shared/workflow'
import {
  visibilityToApiArray,
  apiArrayToVisibility,
  type LocationType,
  type GeneralInfoFormValues,
  type LocationCoverFormValues,
  type EventSettingsFormValues,
  type CollectedEventData,
} from './shared/eventTypes'
import {
  createGeneralInfoSchema,
  createLocationCoverSchema,
  createEventSettingsSchema,
} from './shared/eventValidation'
import type { UpdateEventValue } from 'functions/app/event/update'

import { GeneralInformationStep } from './steps/GeneralInformationStep'
import { LocationCoverStep } from './steps/LocationCoverStep'
import { EventSettingsStep } from './steps/EventSettingsStep'
import { PreviewStep } from './steps/PreviewStep'
import { SuccessStep } from './steps/SuccessStep'

const steps = eventCreationSteps

const defaultGeneralInfo: GeneralInfoFormValues = {
  title: '',
  content: '',
  isGroupEvent: true,
  start: '',
  end: '',
}

const defaultLocationCover: LocationCoverFormValues = {
  address: '',
  meetingLink: '',
}

const defaultEventSettings: EventSettingsFormValues = {
  enableWaitlist: false,
  visibility: 'members',
  reminder: false,
}
const toDateTimeLocal = (ts?: number): string => {
  if (!ts) {
    return ''
  }

  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`
}

const toTimestamp = (value: string): number | undefined => {
  if (!value) return undefined
  const ts = new Date(value).getTime()
  return Number.isNaN(ts) ? undefined : ts
}

const toApiPayload = (info: GeneralInfoFormValues): UpdateEventValue => ({
  ...info,
  start: toTimestamp(info.start),
  end: toTimestamp(info.end) ?? null,
})

const mapLocation = (
  l: LocationCoverFormValues,
  type: LocationType,
): UpdateEventValue =>
  type === 'offline'
    ? { location: { address: l.address, ...(l.geo ? { geo: l.geo } : {}) } }
    : { meetingLink: l.meetingLink }

export const EventCreation = ({
  currentStep: currentStepProp = steps.generalInformation,
  eventId: eventIdProp,
  initiativeId: initiativeIdProp,
}: {
  currentStep?: string
  eventId?: string
  initiativeId?: string
}) => {
  const t = useTranslations().app.dashboard.event.creation
  const v = t.validation
  const client = useClient()
  const [_, navigate] = useLocation()
  const { showFailToast } = useToast()

  const [newEventSlug, setNewEventSlug] = useState<string | null>(
    eventIdProp ?? null,
  )
  const [eventDbId, setEventDbId] = useState<number | null>(null)
  const [initiativeId, setInitiativeId] = useState<number | null>(
    initiativeIdProp ? Number(initiativeIdProp) : null,
  )

  const validSteps: string[] = Object.values(steps)
  const stepFromUrl = validSteps.includes(currentStepProp)
    ? (currentStepProp as EventCreationStep)
    : steps.generalInformation

  const [localStep, setLocalStep] = useState<EventCreationStep>(stepFromUrl)
  const step = newEventSlug ? stepFromUrl : localStep

  const [locationType, setLocationType] = useState<LocationType>('offline')
  const [generalInfo, setGeneralInfo] =
    useState<GeneralInfoFormValues>(defaultGeneralInfo)
  const [locationCover, setLocationCover] =
    useState<LocationCoverFormValues>(defaultLocationCover)
  const [eventSettings, setEventSettings] =
    useState<EventSettingsFormValues>(defaultEventSettings)
  const [coverImages, setCoverImages] = useState<ImagePreview[]>([])
  const [removedCoverImages, setRemovedCoverImages] = useState<number[]>([])
  const [existingImages, setExistingImages] = useState<ExistingImage[]>([])
  const [published, setPublished] = useState(false)
  const [busy, setBusy] = useState(false)
  const [loadingDraft, setLoadingDraft] = useState<boolean>(!!eventIdProp)
  const [exitConfirmationOpen, setExitConfirmationOpen] = useState(false)

  const generalInfoSchema = useMemo(
    () =>
      createGeneralInfoSchema({
        titleMin: v.titleMin,
        contentRequired: v.contentRequired,
        startDateRequired: v.startDateRequired,
        endBeforeStart: v.endBeforeStart,
      }),
    [v],
  )

  const locationCoverSchema = useMemo(
    () =>
      createLocationCoverSchema(locationType, {
        addressRequired: v.addressRequired,
        meetingLinkInvalid: v.meetingLinkInvalid,
      }),
    [locationType, v],
  )

  const eventSettingsSchema = useMemo(
    () =>
      createEventSettingsSchema({
        limitMin: v.limitMin,
        visibilityRequired: v.visibilityRequired,
      }),
    [v],
  )

  // Load an existing draft (resume / after the step-1 create remounts us on the
  // /agenda/create/:slug/:step route) and hydrate every step's form state.
  const refreshDraft = useCallback(() => {
    const slug = newEventSlug ?? eventIdProp
    if (!slug) {
      return
    }

    client
      .call('get-draft-event', { slug })
      .then((draft: DraftEventModel | null) => {
        if (!draft) {
          showFailToast()
          setLoadingDraft(false)
          return
        }

        setEventDbId(draft.id)
        setNewEventSlug(draft.slug)

        if (draft.initiative?.id) {
          setInitiativeId(draft.initiative.id)
        }

        setLocationType(draft.meetingLink ? 'online' : 'offline')
        setGeneralInfo({
          title: draft.title ?? '',
          intro: draft.intro ?? '',
          content: draft.content ?? '',
          isGroupEvent: draft.isGroupEvent ?? true,
          start: toDateTimeLocal(draft.start),
          end: toDateTimeLocal(draft.end),
        })
        setLocationCover({
          address: draft.location?.address ?? '',
          meetingLink: draft.meetingLink ?? '',
        })

        const visibilityFlags: ('members' | 'followers' | 'public')[] = []

        if (draft.isVisiblePublic) visibilityFlags.push('public')
        if (draft.isVisibleMembers) visibilityFlags.push('members')
        if (draft.isVisibleFollowers) visibilityFlags.push('followers')

        setEventSettings({
          limit: draft.limit,
          enableWaitlist: draft.enableWaitlist ?? false,
          visibility: apiArrayToVisibility(visibilityFlags),
          reminder: draft.reminder ?? false,
        })

        setExistingImages(
          (draft.images ?? []).map((i) => ({ id: i.id, src: i.src ?? '' })),
        )

        setLoadingDraft(false)
      })
      .catch(() => {
        showFailToast()
        setLoadingDraft(false)
      })
  }, [client, eventIdProp, newEventSlug, showFailToast])

  useEffect(() => {
    if (eventIdProp) {
      refreshDraft()
    }
  }, [eventIdProp, refreshDraft])

  // Continuous autosave per step once a draft exists. Saves are no-ops before the
  // draft is created (step 1) and never publish — publish is explicit on Preview.
  const updateDraft = useCallback(
    (value: UpdateEventValue) =>
      eventDbId
        ? client.call('update-event', { id: eventDbId, value })
        : Promise.resolve(true),
    [client, eventDbId],
  )

  useAutoSave(generalInfo, (value) => updateDraft(toApiPayload(value)))
  useAutoSave(locationCover, (value) =>
    updateDraft(mapLocation(value, locationType)),
  )
  useAutoSave(eventSettings, (value) =>
    updateDraft({
      ...value,
      visibility: visibilityToApiArray(value.visibility),
    }),
  )

  const setStep = useCallback(
    (next: EventCreationStep) => {
      if (newEventSlug) {
        navigate(pages.eventCreateStep(newEventSlug, next))
      } else {
        setLocalStep(next)
      }
    },
    [newEventSlug, navigate],
  )

  const generalInfoValid = useMemo(
    () => generalInfoSchema.safeParse(generalInfo).success,
    [generalInfoSchema, generalInfo],
  )
  const locationCoverValid = useMemo(
    () => locationCoverSchema.safeParse(locationCover).success,
    [locationCoverSchema, locationCover],
  )
  const eventSettingsValid = useMemo(
    () => eventSettingsSchema.safeParse(eventSettings).success,
    [eventSettingsSchema, eventSettings],
  )

  const currentStepValid = useMemo(() => {
    switch (step) {
      case steps.generalInformation:
        return generalInfoValid
      case steps.locationCover:
        return locationCoverValid
      case steps.eventSettings:
        return eventSettingsValid
      default:
        return generalInfoValid && locationCoverValid && eventSettingsValid
    }
  }, [step, generalInfoValid, locationCoverValid, eventSettingsValid])

  const isNextStepEnabled = useCallback(
    () => currentStepValid && !busy,
    [currentStepValid, busy],
  )

  const isPreviousVisible = useCallback(() => {
    if (published) return false
    return eventCreationWorkflow.indexOf(step) > 0
  }, [step, published])

  const nextStepCallback = useCallback(async () => {
    // Publish: persist the latest values and flip to published server-side.
    if (step === steps.preview) {
      if (!eventDbId) {
        showFailToast()
        return
      }
      setBusy(true)
      try {
        const ok = await client.call('update-event', {
          id: eventDbId,
          value: {
            ...toApiPayload(generalInfo),
            ...mapLocation(locationCover, locationType),
            ...eventSettings,
            visibility: visibilityToApiArray(eventSettings.visibility),
          },
          publish: true,
        })
        if (!ok) {
          showFailToast()
          return
        }
        setPublished(true)
      } catch {
        showFailToast()
      } finally {
        setBusy(false)
      }
      return
    }

    // Step 1 with no draft yet: create the draft. The create and step routes
    // both render <EventCreation>, so React reuses this instance instead of
    // remounting — set the draft id/slug in state here so the step follows the
    // URL and we don't create a second draft on the next click.
    if (step === steps.generalInformation && !newEventSlug) {
      if (!initiativeId) {
        showFailToast()
        return
      }
      setBusy(true)
      try {
        const event = await client.call('create-event', {
          initiativeId,
          ...toApiPayload(generalInfo),
        })
        if (!event?.slug) {
          showFailToast()
          return
        }
        setEventDbId(event.id)
        setNewEventSlug(event.slug)
        navigate(pages.eventCreateStep(event.slug, steps.locationCover))
      } catch {
        showFailToast()
      } finally {
        setBusy(false)
      }
      return
    }

    const currentIndex = eventCreationWorkflow.indexOf(step)
    const next = eventCreationWorkflow[currentIndex + 1]
    setStep(next)
  }, [
    step,
    client,
    eventDbId,
    newEventSlug,
    initiativeId,
    locationType,
    generalInfo,
    locationCover,
    eventSettings,
    setStep,
    navigate,
    showFailToast,
  ])

  const previousStepCallback = useCallback(() => {
    const currentIndex = eventCreationWorkflow.indexOf(step)
    if (currentIndex <= 0) return
    setStep(eventCreationWorkflow[currentIndex - 1])
  }, [step, setStep])

  const collectedData: CollectedEventData = {
    eventType: 'group',
    locationType,
    generalInfo,
    locationCover,
    eventSettings,
    coverImages,
    existingImages,
  }

  const nextButtonLabel = step === steps.preview ? t.publish : t.next
  const nextButtonIcon = step === steps.preview ? undefined : faArrowRight

  const eventUrl =
    typeof window !== 'undefined' && newEventSlug
      ? `${window.location.origin}${pages.event(newEventSlug)}`
      : ''

  const exitTarget = initiativeId
    ? pages.dashboardInitiativeEvents(String(initiativeId))
    : pages.events

  return (
    <CreatePageLayout
      title={t.createEvent}
      onClose={() => published || eventIdProp ? navigate(exitTarget) : setExitConfirmationOpen(true)}
    >
      {published ? (
        <SuccessStep eventTitle={generalInfo.title} eventUrl={eventUrl} />
      ) : (
        <LoaderWrapper loading={loadingDraft}>
          {match(step)
            .with(steps.generalInformation, () => (
              <GeneralInformationStep
                schema={generalInfoSchema}
                defaultValues={generalInfo}
                onValuesChange={setGeneralInfo}
              />
            ))
            .with(steps.locationCover, () => (
              <LocationCoverStep
                schema={locationCoverSchema}
                locationType={locationType}
                setLocationType={setLocationType}
                defaultValues={locationCover}
                eventId={eventDbId}
                existingImages={existingImages}
                onImagesChanged={refreshDraft}
                newImages={coverImages}
                setNewImages={setCoverImages}
                removedImages={removedCoverImages}
                setRemovedImages={setRemovedCoverImages}
                onValuesChange={setLocationCover}
              />
            ))
            .with(steps.eventSettings, () => (
              <EventSettingsStep
                schema={eventSettingsSchema}
                defaultValues={eventSettings}
                onValuesChange={setEventSettings}
              />
            ))
            .with(steps.preview, () => <PreviewStep data={collectedData} />)
            .exhaustive()}
        </LoaderWrapper>
      )}

      {!published && (
        <Row
          alignItems="center"
          style={{
            justifyContent: 'space-between',
            flexDirection: 'row-reverse',
          }}
        >
          <Button
            label={nextButtonLabel}
            icon={nextButtonIcon}
            disabled={!isNextStepEnabled()}
            onClick={() => void nextStepCallback()}
          />
          {isPreviousVisible() && (
            <Button
              label={t.back}
              icon={faArrowLeft}
              iconPosition="left"
              variant="secondary"
              onClick={previousStepCallback}
            />
          )}
        </Row>
      )}

      <Modal
        title={t.closeModalTitle}
        isOpen={exitConfirmationOpen}
        onClose={() => setExitConfirmationOpen(false)}
        onConfirm={() => navigate(exitTarget)}
      >
        <Text.Medium style={{ whiteSpace: 'preserve', width: '20rem' }}>
          {t.closeModalContent}
        </Text.Medium>
      </Modal>
    </CreatePageLayout>
  )
}
